import React, { useEffect, useRef } from "react";

import { Outlet, useLocation } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useDispatch } from "react-redux";
import Sidebar from "../components/Sidebar";
import Navbar from "../pages/Navbar";
import { useNetworkStatus, getStoredValue } from "../services/logics";
import InternetConnectionIssue from "../components/InternetConnectionIssue";
import GlobalIssuesDetectedModal from "../components/policy/GlobalIssuesDetectedModal.jsx";
import GlobalDocumentPreviewModal from "../components/policy/GlobalDocumentPreviewModal.jsx";
import GlobalTemplateConflictBanner from "../components/TemplatePageComponents/GlobalTemplateConflictBanner.jsx";
import GlobalTemplateConflictModal from "../components/TemplatePageComponents/GlobalTemplateConflictModal.jsx";
import GlobalTemplateConflictsListModal from "../components/TemplatePageComponents/GlobalTemplateConflictsListModal.jsx";
import GlobalEmptyInboxBanner from "../components/Emails/GlobalEmptyInboxBanner.jsx";
import WhatsAppFloatingButton from "../components/WhatsAppFloatingButton.jsx";
import { fetchTemplateConflicts } from "../redux/templateConflictsSlice.js";
import { getSSOProvider } from "../utils/ssoProvider";

const isSpLite = getSSOProvider() === "sp_lite";

const Layout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isOnline, updateNetworkStatus } = useNetworkStatus();
  const hasFetchedConflicts = useRef(false);
  // The backend's "use-draft-v2" LD flag (same one draft_generator.py checks) - a
  // conflict between two templates can only actually confuse the AI's automatic
  // template-matching for a store whose drafts run through that pipeline. A store
  // still on the old, example-based pipeline never auto-selects a template at all,
  // so the warning would be a false alarm for them regardless of how many
  // conflicting templates they've created (e.g. in preparation for switching over).
  const { useDraftV2 } = useFlags();

  useEffect(() => {
    updateNetworkStatus();
  }, [location]);

  useEffect(() => {
    // Layout stays mounted across route changes (only <Outlet /> swaps) and renders
    // for storeless/not-yet-onboarded accounts too (welcome-page, select-store), where
    // this store-scoped endpoint 401s. Re-check on each navigation until the account
    // actually has a store and is onboarded, then fetch once and stop.
    if (hasFetchedConflicts.current) return;
    const storeId = getStoredValue("storeid");
    const hasStore = storeId && storeId !== "undefined" && storeId !== "null";
    const isOnboarded = getStoredValue("isonboarded") === "true";
    if (hasStore && isOnboarded) {
      hasFetchedConflicts.current = true;
      dispatch(fetchTemplateConflicts());
    }
  }, [dispatch, location.pathname]);
  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key === "isLoggedIn" && event.newValue === "false") {
        // Log out this tab if isLoggedIn is set to false
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/signin";
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isWelcomePage = location.pathname.includes("/welcome-page");
  // const isAddClientPage = location.pathname.includes("/add-client");
  const isSelectStorePage = location.pathname.includes("/select-store");
  const isIntroAppPage = location.pathname.includes("/intro-app");
  const isOnboardingScreen = isWelcomePage || isSelectStorePage || isIntroAppPage;
  return (
    <>
      <Navbar />
      {/* Also excluded on onboarding screens (welcome-page, select-store): a user
          switching stores there can still be carrying conflict data fetched for
          the PREVIOUS store in redux, which would otherwise flash a conflict
          banner for a store they haven't even entered yet. */}
      {useDraftV2 && !isOnboardingScreen && <GlobalTemplateConflictBanner />}
      {!isOnboardingScreen && <GlobalEmptyInboxBanner />}
      <div className="flex">
        {!isOnboardingScreen && <Sidebar />}
        {isOnline ? (
          <div className="flex-3  w-full h-full">
            <Outlet />
          </div>
        ) : (
          <InternetConnectionIssue />
        )}
      </div>
      {isSpLite && !isOnboardingScreen && <WhatsAppFloatingButton />}
      <GlobalIssuesDetectedModal />
      <GlobalDocumentPreviewModal />
      <GlobalTemplateConflictModal />
      <GlobalTemplateConflictsListModal />
    </>
  );
};

export default Layout;
